import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectIsNightMode, toggleMode } from '../../../redux/slices/modeSlice';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import SellerSidebar from '../sellerSidebar';
import SellerHeader from '../sellerHeader';
import './sellerLayout.css';

export default function SellerLayout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const isNightMode = useSelector(selectIsNightMode);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect admin users to admin dashboard; affiliate-only users away from seller dashboard
  useEffect(() => {
    const auth = getAuthLocalStorage();
    const userType = (auth?.userType || localStorage.getItem('userType') || '').toLowerCase();
    const isAdmin = userType === 'admin' || userType === 'sub-admin';
    const isSeller = auth?.isSeller !== false;
    const isAffiliateOnly = auth?.isAffiliate === true && !isSeller;

    if (isAdmin) {
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    // Affiliate-only: block all seller routes except /seller/affiliate/*
    const isAffiliatePath = location.pathname.startsWith('/seller/affiliate');
    if (isAffiliateOnly && location.pathname.startsWith('/seller') && !isAffiliatePath) {
      navigate('/seller/affiliate/dashboard', { replace: true });
    }
  }, [navigate, location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };

    handleResize(); // Check on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMenuClick = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleDarkModeToggle = () => {
    dispatch(toggleMode());
  };

  // Check if user is admin - if so, don't render seller layout (will redirect)
  const auth = getAuthLocalStorage();
  const userType = (auth?.userType || localStorage.getItem('userType') || '').toLowerCase();
  const isAdmin = userType === 'admin' || userType === 'sub-admin';
  
  if (isAdmin) {
    return null; // Will redirect in useEffect
  }

  // Calculate margin for main content
  const getMainMargin = () => {
    if (isMobile) return '0';
    return sidebarCollapsed ? '80px' : '240px';
  };

  return (
    <div className="seller-panel-layout">
      <SellerSidebar
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        mobileOpen={mobileMenuOpen}
        setMobileOpen={setMobileMenuOpen}
      />
      
      <main className="seller-panel-main" style={{ marginLeft: getMainMargin() }}>
        <SellerHeader
          onMenuClick={handleMenuClick}
          darkMode={isNightMode}
          setDarkMode={handleDarkModeToggle}
        />
        
        <div className="seller-panel-content">
          <div className="seller-page-container">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
